import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SmartStopLoss.css';

const FlagPennantGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="article-page">
      {/* Back Navigation */}
      <div className="article-nav">
        <button className="back-button" onClick={() => navigate('/learning-hub')}>
          ← Back to Learning Hub
        </button>
      </div>

      {/* Article Header */}
      <header className="article-header">
        <div className="article-badge">CONTINUATION PATTERN</div>
        <h1 className="article-title">How to Trade Bullish Flag & Pennant Patterns</h1>
        <div className="article-meta">
          <span className="meta-item">📅 Updated December 2024</span>
          <span className="meta-item">⏱️ 11 min read</span>
          <span className="meta-item">🎯 High Probability Breakout</span>
        </div>
      </header>

      {/* Article Content */}
      <article className="article-content">

        {/* Introduction */}
        <section className="content-section">
          <h2>What are Flag & Pennant Patterns?</h2>
          <div className="ai-badge-container">
            <span className="ai-badge">📊 Pattern Detected by Screener</span>
            <span className="market-badge">📈 Best for Trending Markets</span>
          </div>
          <p className="lead-text">
            Bullish Flag and Pennant patterns are powerful continuation patterns that signal the resumption of an
            uptrend after a brief consolidation. These patterns occur when a stock is in a strong uptrend, pauses
            to "rest," then continues moving higher.
          </p>

          <p>
            Both patterns indicate that buyers are taking a breather before pushing prices higher again. The key
            difference is in their shape: flags have parallel trend lines, while pennants form converging trend lines
            that resemble a small triangle or pennant.
          </p>

          <div className="highlight-box">
            <strong>Key Characteristics:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li><strong>Flagpole:</strong> Sharp upward move (nearly vertical rise)</li>
              <li><strong>Consolidation:</strong> Brief pause that moves counter to the trend</li>
              <li><strong>Breakout:</strong> Continuation in the original direction with volume</li>
              <li><strong>Success Rate:</strong> High risk-reward ratio when traded correctly</li>
            </ul>
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">🏴</div>
              <p className="placeholder-text">Flag vs Pennant Pattern Comparison</p>
              <p className="placeholder-desc">Side-by-side illustration showing flag (parallel) and pennant (converging) formations</p>
            </div>
          </div>
        </section>

        {/* Pattern Components */}
        <section className="content-section">
          <h2>Understanding the Pattern Components</h2>

          <h3>1. The Flagpole (Strong Upward Move)</h3>
          <div className="method-card">
            <p>The flagpole is the initial strong movement that precedes the consolidation:</p>
            <ul>
              <li><strong>Steep and nearly vertical rise</strong> in price</li>
              <li>Usually accompanied by <strong>high volume</strong></li>
              <li>Represents strong buying momentum</li>
              <li>Should be a clear, decisive move (minimum 10-15% in a few days)</li>
              <li>The taller the flagpole, the more powerful the pattern</li>
            </ul>

            <div className="example-box">
              <h4>Example Flagpole:</h4>
              <p><strong>Day 1:</strong> ₹400</p>
              <p><strong>Day 5:</strong> ₹460 (15% gain in 5 days)</p>
              <p><strong>Volume:</strong> 2-3x average during the rise</p>
              <p className="result">✅ Strong flagpole formation!</p>
            </div>
          </div>

          <h3>2. Bullish Flag Consolidation</h3>
          <div className="method-card">
            <p>Flag consolidation has <strong>parallel trend lines</strong> sloping downward:</p>
            <ul>
              <li>Forms after the sharp upward move</li>
              <li>Shows consecutive <strong>lower highs and lower lows</strong></li>
              <li>Downward or sideways drift (slight pullback)</li>
              <li>Volume <strong>decreases significantly</strong> during flag formation</li>
              <li>Typically lasts <strong>1-3 weeks</strong> (short consolidation)</li>
            </ul>

            <div className="tip-box">
              <strong>💡 Key Insight:</strong> The flag should slope <strong>against</strong> the prevailing trend.
              In a bullish flag, expect a downward-sloping consolidation. This shows profit-taking, not trend reversal.
            </div>
          </div>

          <h3>3. Pennant Consolidation</h3>
          <div className="method-card">
            <p>Pennant has <strong>converging trend lines</strong> forming a small symmetrical triangle:</p>
            <ul>
              <li>Two converging trend lines that meet at a point</li>
              <li>Creates a symmetrical triangle shape</li>
              <li>Price oscillates within narrowing range</li>
              <li>Volume diminishes during formation</li>
              <li>Usually lasts <strong>1-2 weeks</strong> (shorter than flags)</li>
            </ul>

            <div className="example-box">
              <h4>Flag vs Pennant Duration:</h4>
              <p><strong>Flag:</strong> 1-3 weeks (parallel lines)</p>
              <p><strong>Pennant:</strong> Few days to 2 weeks (converging lines)</p>
              <p className="insight">Pennants are typically shorter and tighter than flags</p>
            </div>
          </div>

          <h3>4. Volume Pattern (Critical!)</h3>
          <div className="method-card">
            <p>Volume behavior confirms the pattern validity:</p>
            <ul>
              <li><strong>Flagpole:</strong> High volume during initial surge</li>
              <li><strong>Consolidation:</strong> Declining volume (shows lack of selling pressure)</li>
              <li><strong>Breakout:</strong> Volume spike 30-40% above average minimum</li>
              <li>Without proper volume, pattern has higher failure rate</li>
            </ul>
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">📊</div>
              <p className="placeholder-text">Complete Flag Pattern with Volume</p>
              <p className="placeholder-desc">Shows flagpole, consolidation, and breakout with volume bars</p>
            </div>
          </div>
        </section>

        {/* How to Identify */}
        <section className="content-section">
          <h2>How to Identify Valid Flag & Pennant Patterns</h2>

          <div className="trailing-rules">
            <div className="rule-card">
              <div className="rule-number">Step 1</div>
              <h4>Strong Uptrend Requirement</h4>
              <p>Our screener filters for existing uptrends with:</p>
              <ul>
                <li>Stock making higher highs and higher lows</li>
                <li>Price above key moving averages (20 EMA, 50 SMA)</li>
                <li>Strong momentum preceding the pattern</li>
              </ul>
            </div>

            <div className="rule-card">
              <div className="rule-number">Step 2</div>
              <h4>Flagpole Detection</h4>
              <p>The screener identifies sharp, nearly vertical price rises:</p>
              <ul>
                <li>Minimum 10-15% gain in short period (few days to 2 weeks)</li>
                <li>Accompanied by high volume</li>
                <li>Clear, decisive moves (choppy patterns filtered out)</li>
              </ul>
            </div>

            <div className="rule-card">
              <div className="rule-number">Step 3</div>
              <h4>Consolidation Recognition</h4>
              <p>Our screener identifies flag or pennant formations:</p>
              <ul>
                <li><strong>Flag:</strong> Parallel downward-sloping lines</li>
                <li><strong>Pennant:</strong> Converging lines forming triangle</li>
                <li>Volume decreasing during consolidation</li>
                <li>Retracements over 50% of flagpole are excluded</li>
              </ul>
            </div>

            <div className="rule-card">
              <div className="rule-number">Step 4</div>
              <h4>Confirm Pattern Validity</h4>
              <p>Ensure all criteria are met:</p>
              <ul>
                <li>✅ Consolidation is brief (1-3 weeks max)</li>
                <li>✅ Volume declined during consolidation</li>
                <li>✅ Pattern forms after strong move</li>
                <li>✅ Price hasn't broken below flag/pennant support</li>
              </ul>
            </div>
          </div>

          <div className="warning-box">
            <strong>⚠️ Invalid Patterns:</strong> If the consolidation lasts more than 3-4 weeks, retraces more
            than 50% of the flagpole, or shows increasing volume during consolidation, the pattern loses validity
            and has a higher chance of failure.
          </div>
        </section>

        {/* Entry Strategy */}
        <section className="content-section">
          <h2>Entry Strategies for Flag & Pennant Breakouts</h2>

          <h3>Entry Method 1: Breakout Entry (Aggressive)</h3>
          <div className="method-card">
            <p>Enter when price breaks above the upper trendline of the flag/pennant:</p>
            <ul>
              <li>Buy when price closes above upper resistance</li>
              <li>Must have <strong>volume spike (50-100% above average)</strong></li>
              <li>Enter immediately after breakout candle closes</li>
              <li>Best for capturing the full move</li>
            </ul>

            <div className="example-box">
              <h4>Breakout Entry Example:</h4>
              <p><strong>Flag Upper Resistance:</strong> ₹465</p>
              <p><strong>Breakout Day:</strong> Closes at ₹470 with 2x volume</p>
              <p><strong>Entry:</strong> Buy at ₹470-472</p>
              <p className="result">✅ Confirmed breakout with volume!</p>
            </div>
          </div>

          <h3>Entry Method 2: Pullback Entry (Conservative)</h3>
          <div className="method-card">
            <p>Wait for price to pull back and retest the breakout level:</p>
            <ul>
              <li>After initial breakout, wait for minor pullback</li>
              <li>Enter when price bounces off the breakout level (now support)</li>
              <li>Provides better entry price and tighter stop</li>
              <li>Not all breakouts will pull back</li>
            </ul>

            <div className="tip-box">
              <strong>💡 Pro Tip:</strong> Split your position - buy 50% on breakout, and wait for pullback to
              add the remaining 50%. This balances missing the move vs. getting a better entry.
            </div>
          </div>

          <h3>Entry Confirmation Checklist</h3>
          <div className="method-card">
            <p>Before entering, confirm these points:</p>
            <ul>
              <li>✅ <strong>Volume surges</strong> on breakout (minimum 30-40% above average)</li>
              <li>✅ Price closes <strong>decisively above</strong> upper trendline</li>
              <li>✅ Pattern formed after <strong>strong uptrend</strong></li>
              <li>✅ Broader <strong>market is bullish</strong> (not fighting downtrend)</li>
              <li>✅ No major resistance levels nearby</li>
            </ul>
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">🎯</div>
              <p className="placeholder-text">Flag Breakout Entry Points</p>
              <p className="placeholder-desc">Shows both aggressive breakout entry and conservative pullback entry</p>
            </div>
          </div>
        </section>

        {/* Profit Targets */}
        <section className="content-section">
          <h2>Profit Targets and Exit Strategy</h2>

          <h3>Measured Move Method (Most Common)</h3>
          <div className="method-card">
            <p>Project the <strong>height of the flagpole</strong> from the breakout point:</p>
            <ul>
              <li>Measure the distance from flagpole bottom to top</li>
              <li>Add that distance to the breakout point</li>
              <li>This gives you the minimum price target</li>
            </ul>

            <div className="example-box">
              <h4>Measured Move Calculation:</h4>
              <p><strong>Flagpole Start:</strong> ₹400</p>
              <p><strong>Flagpole Top:</strong> ₹460 (height = ₹60)</p>
              <p><strong>Breakout Point:</strong> ₹470</p>
              <p><strong>Price Target:</strong> ₹470 + ₹60 = ₹530</p>
              <p className="result">✅ Target: ₹530 (12.8% gain from entry)</p>
            </div>
          </div>

          <h3>Risk-Reward Ratio Method</h3>
          <div className="method-card">
            <p>Target at least <strong>2-3x your risk</strong>:</p>
            <ul>
              <li>If risking ₹25 per share, target ₹50-75 gain</li>
              <li>Ensures positive expectancy over multiple trades</li>
              <li>Minimum 2:1, ideal 3:1 risk-reward</li>
            </ul>
          </div>

          <h3>Scaling Out Strategy</h3>
          <div className="method-card">
            <ol>
              <li><strong>Sell 1/3 at measured move target</strong> - Locks in profit</li>
              <li><strong>Sell 1/3 at 1.5x measured move</strong> - Takes more profit</li>
              <li><strong>Trail stop on final 1/3</strong> - Lets winners run</li>
            </ol>
            <p style={{ marginTop: '16px' }}>
              This approach balances taking profits with staying in for larger moves.
            </p>
          </div>

          <div className="highlight-box">
            <strong>Realistic Expectations:</strong> Flag and pennant breakouts often achieve their measured move
            target 60-70% of the time when accompanied by strong volume. This makes them one of the most reliable
            continuation patterns in swing trading.
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">💰</div>
              <p className="placeholder-text">Measured Move Target Calculation</p>
              <p className="placeholder-desc">Visual demonstration of measuring flagpole and projecting target</p>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="content-section">
          <h2>Common Mistakes to Avoid</h2>

          <div className="mistake-card">
            <h4>❌ Mistake #1: Entering Without Volume Confirmation</h4>
            <p>
              Breakouts without volume surges often fail and reverse quickly.
              <strong> Always wait for 30-40% volume spike minimum before entering.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #2: Trading Flags in Downtrends</h4>
            <p>
              Bullish flags only work in uptrends. Trading them in downtrends or sideways markets has poor success.
              <strong> Ensure stock is in a clear uptrend before trading the pattern.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #3: Entering Too Early (Anticipation)</h4>
            <p>
              Jumping in before the breakout risks getting caught in false moves or deeper consolidation.
              <strong> Wait for confirmed breakout above resistance with volume.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #4: Ignoring Pattern Duration</h4>
            <p>
              Consolidations lasting more than 3-4 weeks lose their power and often fail.
              <strong> Avoid patterns with extended consolidation periods.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #5: Weak Flagpole</h4>
            <p>
              Flags formed after weak, choppy moves don't have the momentum to continue.
              <strong> Require a strong, sharp flagpole (minimum 10-15% in few days).</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #6: Deep Retracement</h4>
            <p>
              If consolidation retraces more than 50% of the flagpole, pattern validity decreases.
              <strong> Prefer shallow consolidations (20-40% retracement).</strong>
            </p>
          </div>
        </section>

        {/* Real Examples */}
        <section className="content-section">
          <h2>Real Trading Examples</h2>

          <div className="method-card">
            <h4>Example 1: Bullish Flag Success</h4>
            <ul>
              <li><strong>Stock:</strong> TATAMOTORS</li>
              <li><strong>Flagpole:</strong> ₹650 to ₹750 in 7 days (15% gain)</li>
              <li><strong>Flag Formation:</strong> 12 days of downward consolidation to ₹720</li>
              <li><strong>Breakout:</strong> ₹755 with 2.5x volume</li>
              <li><strong>Target:</strong> ₹755 + ₹100 (flagpole height) = ₹855</li>
              <li><strong>Result:</strong> Reached ₹850 in 3 weeks (12.6% gain)</li>
            </ul>
          </div>

          <div className="method-card">
            <h4>Example 2: Pennant Pattern</h4>
            <ul>
              <li><strong>Stock:</strong> RELIANCE</li>
              <li><strong>Flagpole:</strong> ₹2,400 to ₹2,600 in 5 days (8.3% gain)</li>
              <li><strong>Pennant:</strong> 8 days of converging consolidation</li>
              <li><strong>Breakout:</strong> ₹2,610 with strong volume</li>
              <li><strong>Target:</strong> ₹2,610 + ₹200 = ₹2,810</li>
              <li><strong>Result:</strong> Hit ₹2,800 in 2 weeks (7.3% gain)</li>
            </ul>
          </div>

          <div className="highlight-box">
            <strong>Pattern Statistics:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li><strong>Success rate:</strong> 60-70% when all criteria are met</li>
              <li><strong>Average gain:</strong> 10-20% to measured move target</li>
              <li><strong>Timeframe:</strong> Targets typically reached in 2-4 weeks</li>
              <li><strong>Best markets:</strong> Strong bull markets with high momentum</li>
            </ul>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="content-section">
          <h2>Flag & Pennant Trading Quick Reference</h2>

          <div className="reference-table">
            <table>
              <thead>
                <tr>
                  <th>Element</th>
                  <th>Bullish Flag</th>
                  <th>Bullish Pennant</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Shape</td>
                  <td>Parallel downward lines</td>
                  <td>Converging triangle</td>
                </tr>
                <tr>
                  <td>Duration</td>
                  <td>1-3 weeks</td>
                  <td>Few days to 2 weeks</td>
                </tr>
                <tr>
                  <td>Volume Pattern</td>
                  <td>High → Low → High</td>
                  <td>High → Low → High</td>
                </tr>
                <tr>
                  <td>Target</td>
                  <td>Flagpole height added to breakout</td>
                  <td>Flagpole height added to breakout</td>
                </tr>
                <tr>
                  <td>Best Entry</td>
                  <td>Breakout above upper line</td>
                  <td>Breakout above upper line</td>
                </tr>
                <tr>
                  <td>Success Rate</td>
                  <td>60-70% with volume</td>
                  <td>60-70% with volume</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Summary */}
        <section className="content-section summary-section">
          <h2>Key Takeaways</h2>
          <ul className="takeaway-list">
            <li>✅ Flags and pennants are continuation patterns that signal trend resumption</li>
            <li>✅ Both require a strong flagpole (10-15%+ sharp move) before consolidation</li>
            <li>✅ Volume MUST surge 30-40%+ on breakout - non-negotiable for success</li>
            <li>✅ Target measured move: add flagpole height to breakout point</li>
            <li>✅ Pattern works best in strong uptrends during bull markets</li>
            <li>✅ Consolidation should be brief (1-3 weeks max) and shallow (under 50% retracement)</li>
            <li>✅ Success rate is 60-70% when all criteria are met with volume confirmation</li>
          </ul>

          <div className="final-note">
            <strong>Remember:</strong> Flag and pennant patterns are among the most reliable continuation patterns
            for swing trading. Our Bullish Flag & Pennant Screener automatically identifies complete patterns with
            proper volume confirmation, saving you hours of chart scanning. For risk management and stop-loss techniques,
            check our dedicated Smart Stop-Loss guide.
          </div>
        </section>

      </article>

      {/* Related Articles */}
      <section className="related-articles">
        <h3>Continue Learning</h3>
        <div className="related-grid">
          <div className="related-card" onClick={() => navigate('/learning-hub/vcp-guide')}>
            <div className="related-icon">🎯</div>
            <h4>VCP Pattern Guide</h4>
            <p>Master the Volatility Contraction Pattern</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub/narrow-range-guide')}>
            <div className="related-icon">📏</div>
            <h4>Narrow Range Patterns</h4>
            <p>Trade NR4 and NR7 breakout setups</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub')}>
            <div className="related-icon">📚</div>
            <h4>View All Guides</h4>
            <p>Explore all swing trading tutorials</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="article-cta">
        <h3>Ready to Find Flag & Pennant Setups?</h3>
        <p>Use our screener to find stocks forming bullish flag and pennant patterns</p>
        <button className="cta-button" onClick={() => navigate('/screener/bullish-flag-pennant')}>
          Try Flag & Pennant Screener →
        </button>
      </section>

    </div>
  );
};

export default FlagPennantGuide;
